import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Footer from './components/Footer';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DiscussionDetailPage from './pages/DiscussionDetailPage';
import RegisterPage from './pages/RegisterPage';
import SearchPage from './pages/SearchPage';
import ComposeDiscussionPage from './pages/ComposeDiscussionPage';
import ManageUsersPage from './pages/ManageUsersPage';
import ManageRolePermissionsPage from './pages/ManageRolePermissionsPage';
import InviteUsersPage from './pages/InviteUsersPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ArchivePage from './pages/ArchivePage';
import StaffOpsCenterPage from './pages/StaffOpsCenterPage';
import ReportWeatherPage from './pages/ReportWeatherPage';
import StaffReportsPage from './pages/StaffReportsPage';
import StaffDraftQueuePage from './pages/StaffDraftQueuePage';
import TemplateLibraryPage from './pages/TemplateLibraryPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import NotFoundPage from './pages/NotFoundPage';
import SettingsPage from './pages/SettingsPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import TermsPage from './pages/TermsPage';
import PublicStatusPage from './pages/PublicStatusPage';
import './App.css';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/archive" element={<ArchivePage />} />
        <Route path="/report" element={<ReportWeatherPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/status" element={<PublicStatusPage />} />
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/register" element={isAuthenticated ? <Navigate to="/" /> : <RegisterPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/compose" element={<ComposeDiscussionPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/account" element={<AccountSettingsPage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="/staff/ops-center" element={<StaffOpsCenterPage />} />
        <Route path="/staff/drafts" element={<StaffDraftQueuePage />} />
        <Route path="/staff/reports" element={<StaffReportsPage />} />
        <Route path="/staff/templates" element={<TemplateLibraryPage />} />
        <Route path="/invite-users" element={<InviteUsersPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="/manage-users" element={<ManageUsersPage />} />
        <Route path="/manage-permissions" element={<ManageRolePermissionsPage />} />
        <Route path="/discussions/:permalink" element={<DiscussionDetailPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
    </>
  );
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<string>(() => {
    const storedTheme = localStorage.getItem('glsn-theme');
    return storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'storm' || storedTheme === 'midnight' || storedTheme === 'emerald' || storedTheme === 'contrast'
      ? storedTheme
      : 'dark';
  });

  useEffect(() => {
    document.body.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const handleThemeChange = () => setTheme(localStorage.getItem('glsn-theme') || 'dark');
    window.addEventListener('glsn-theme-change', handleThemeChange);
    window.addEventListener('storage', handleThemeChange);
    return () => {
      window.removeEventListener('glsn-theme-change', handleThemeChange);
      window.removeEventListener('storage', handleThemeChange);
    };
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;
